/**
 * useUpdateFieldSubmit — shared submit logic for updating a single field
 * (one property or one association) across a selection of entities. The
 * selection can be a single entity (inline "Update {field}" edit on a record)
 * or many (bulk update from a list); both go through the same update-property /
 * update-association endpoints, discriminated by the selection shape.
 *
 * Encapsulates: building the selection discriminator (entity_ids / filters /
 * all), the sync `has_more` chunk loop (single-chunk in practice given
 * threshold == batch, loop is a safety net), async dispatch, and toast/error
 * handling. The caller discriminates via the `kind` field of `submit` args.
 *
 * Partial-failure counts: if the server returns failures (`has_more` loop
 * hitting the iteration cap, or zero records updated), the incomplete toast is
 * shown instead of a success toast so users know to retry.
 */

import { useCallback, useMemo, useState } from 'react';
import { toast } from 'sonner';

import type {
  AssociationTypeName,
  BulkActionSelection,
} from '@/lib/crm/core/CRMObjectClient';
import { createCRMObjectClient } from '@/lib/crm/core/CRMObjectClient';
import { translateBulkUpdateError } from '@/lib/crm/core/errors';
import type { CrmObject, EntityTypeName, FilterGroup } from '@/types/crm';

const MAX_SYNC_ITERATIONS = 20;

export interface UpdateFieldSubmitMessages {
  /** Toast title on full sync success. */
  successTitle: string;
  /** Toast description on full sync success. */
  successDescription: string;
  /** Toast error shown when the sync loop hits its iteration cap (partial). */
  incomplete: string;
}

export type UpdateFieldSubmitArgs =
  | {
      kind: 'property';
      propertyTypeName: string;
      value: unknown;
      messages: UpdateFieldSubmitMessages;
    }
  | {
      kind: 'association';
      associationTypeName: AssociationTypeName;
      targetEntityId: string;
      messages: UpdateFieldSubmitMessages;
    };

export interface UseUpdateFieldSubmitParams {
  orgId: number;
  entityTypeName: EntityTypeName;
  selectedObjects: CrmObject[];
  isSelectAllMode: boolean;
  filterGroups?: FilterGroup[];
  excludedEntityIds?: string[];
  search?: string;
  searchProperties?: string[];
  onAsyncDispatched: (jobId: number, totalCount: number) => void;
  onSyncComplete: () => void;
  onClose: () => void;
}

export interface UseUpdateFieldSubmitResult {
  submit: (args: UpdateFieldSubmitArgs) => Promise<void>;
  isSubmitting: boolean;
}

export function useUpdateFieldSubmit({
  orgId,
  entityTypeName,
  selectedObjects,
  isSelectAllMode,
  filterGroups,
  excludedEntityIds,
  search,
  searchProperties,
  onAsyncDispatched,
  onSyncComplete,
  onClose,
}: UseUpdateFieldSubmitParams): UseUpdateFieldSubmitResult {
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Stable selection input — pick the discriminator that matches intent:
  //   - explicit row selection → entity_ids
  //   - select-all + positive selector (filterGroups or search) → filters
  //   - select-all with no selector → all (explicit opt-in to unscoped)
  const selection = useMemo<BulkActionSelection>(() => {
    if (!isSelectAllMode) {
      return {
        kind: 'entity_ids',
        entityIds: selectedObjects.map(o => o.entity_id),
      };
    }
    const hasFilters = !!filterGroups && filterGroups.length > 0;
    const hasSearch = search != null && search !== '';
    if (hasFilters || hasSearch) {
      return {
        kind: 'filters',
        filterGroups,
        excludedEntityIds,
        search,
        searchProperties,
      };
    }
    return { kind: 'all', excludedEntityIds };
  }, [
    isSelectAllMode,
    filterGroups,
    excludedEntityIds,
    search,
    searchProperties,
    selectedObjects,
  ]);

  const submit = useCallback(
    async (args: UpdateFieldSubmitArgs) => {
      if (isSubmitting) {
        return;
      }

      setIsSubmitting(true);
      const client = createCRMObjectClient(orgId, entityTypeName);

      const dispatchChunk = () => {
        if (args.kind === 'property') {
          return client.bulkUpdateProperty(
            entityTypeName,
            args.propertyTypeName,
            args.value,
            selection
          );
        }
        return client.bulkUpdateAssociation(
          entityTypeName,
          args.associationTypeName,
          args.targetEntityId,
          selection
        );
      };

      try {
        const firstResponse = await dispatchChunk();

        if (firstResponse.mode === 'async') {
          onAsyncDispatched(firstResponse.job_id, firstResponse.total_count);
          onClose();
          return;
        }

        // Only the select-all / filter-based path can produce `has_more` — the
        // explicit entity_ids path always processes the full set in one chunk.
        let hasMore = firstResponse.has_more;
        let iterations = 1;
        let hitIterationCap = false;
        let totalUpdated =
          firstResponse.updated_count ?? firstResponse.changed_count ?? 0;

        while (hasMore) {
          iterations += 1;
          if (iterations > MAX_SYNC_ITERATIONS) {
            hitIterationCap = true;
            break;
          }
          const response = await dispatchChunk();

          if (response.mode === 'async') {
            onAsyncDispatched(response.job_id, response.total_count);
            onClose();
            return;
          }

          totalUpdated += response.updated_count ?? response.changed_count ?? 0;
          hasMore = response.has_more;
        }

        const { messages } = args;

        if (hitIterationCap || totalUpdated === 0) {
          // Partial / no-op — report so the user knows to retry. `totalUpdated
          // === 0` covers the case where every matching record was rejected
          // (e.g. per-record permission failures) and no events landed; the
          // success toast would mislead the user into thinking work completed.
          toast.error(messages.incomplete);
        } else {
          toast.success(messages.successTitle, {
            description: messages.successDescription,
          });
        }
        onSyncComplete();
        onClose();
      } catch (err) {
        const errorType = (err as { response?: { error_type?: string } })
          ?.response?.error_type;
        toast.error(translateBulkUpdateError(errorType));
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      isSubmitting,
      orgId,
      entityTypeName,
      selection,
      onAsyncDispatched,
      onSyncComplete,
      onClose,
    ]
  );

  return { submit, isSubmitting };
}
